import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { Promotion } from "admin/types/promotion";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { PromotionApplicableConditionTypes } from "../../../constants/promotionApplicableConditionTypes";
import { PromotionTypes } from "../../../constants/promotionTypes";

const key = "hotelPromotion";
const adapter = createEntityAdapter<Promotion.Promotion>();

const initialStateValues: Promotion.PromotionState = {
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchHotelPromotions = createAsyncThunk<
  {
    promotions: Promotion.Promotion[];
  },
  {
    context: PageContext.PageContext;
    hotelId: string;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotelPromotions`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      hotelId,
      param,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/promotions.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/promotions.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      promotions: result.data.promotions,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchHotelPromotion = createAsyncThunk<
  {
    promotion: Promotion.Promotion;
  },
  {
    context: PageContext.PageContext;
    id: number;
    hotelId: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchHotelPromotion`, async (args, { rejectWithValue }) => {
  try {
    const {
      context,
      id,
      hotelId,
      isServerSide = false,
      headers,
      locale,
    } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/promotions/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/promotions/${id}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      promotion: result.data.promotion,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postHotelPromotion = createAsyncThunk<
  { promotion: Promotion.Promotion },
  {
    context: PageContext.PageContext;
    hotelId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postHotelPromotion`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/promotions.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/promotions.json`;
    const result = await createWrapper().post(url, { promotion: params });
    return { promotion: result.data.promotion };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putHotelPromotion = createAsyncThunk<
  { promotion: Promotion.Promotion },
  {
    context: PageContext.PageContext;
    id: number;
    hotelId: string;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putHotelPromotion`, async (args, { rejectWithValue }) => {
  try {
    const { context, hotelId, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/hotels/${hotelId}/promotions/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/hotels/${hotelId}/promotions/${id}.json`;
    const result = await createWrapper().put(url, { promotion: params });
    return { promotion: result.data.promotion };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const hotelPromotionSlice = createSlice({
  name: key,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(HYDRATE, (state, action: any) => {
        return { ...state, ...action.payload.hotelPromotion };
      })
      .addCase(fetchHotelPromotions.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.promotions);
      })
      .addCase(fetchHotelPromotion.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.promotion);
      })
      .addCase(postHotelPromotion.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.promotion);
      })
      .addCase(putHotelPromotion.fulfilled, (state, action) => {
        adapter.upsertOne(state, action.payload.promotion);
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: hotelPromotionByIdSelector,
  selectIds: hotelPromotionIdsSelector,
  selectEntities: hotelPromotionEntitiesSelector,
  selectAll: hotelPromotionsSelector,
  selectTotal: totalHotelPromotionsSelector,
} = adapter.getSelectors((state: RootState) => state.hotelPromotion);

const stateSelector = (state: RootState) => state[key];

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const convertHotelPromotionFormValuesToParams = (data: any) => {
  const params = { ...data };
  const promotionType = params.promotion_type;
  if (promotionType !== PromotionTypes.promotionCode && params.code) {
    delete params.code;
  }
  if (promotionType !== PromotionTypes.consecutiveStay) {
    if (params.stay_from) {
      delete params.stay_from;
    }
    if (params.stay_to) {
      delete params.stay_to;
    }
  }

  // 不要なidsの場合[]で送信する
  if (params.applicable_condition === PromotionApplicableConditionTypes.hotel) {
    params.hotel_plan_ids = [];
    params.hotel_room_ids = [];
  } else if (
    params.applicable_condition === PromotionApplicableConditionTypes.hotelPlan
  ) {
    params.hotel_room_ids = [];
  } else if (
    params.applicable_condition === PromotionApplicableConditionTypes.hotelRoom
  ) {
    params.hotel_plan_ids = [];
  }
  return params;
};

export const convertHotelPromotionToFormValues = (data: any) => {
  const values = { ...data };
  if (values.hotels) {
    delete values.hotels;
  }
  if (values.hotelPlans) {
    values.hotel_plan_ids = values.hotelPlans.map((hotelPlan) =>
      String(hotelPlan.id),
    );
    delete values.hotelPlans;
  }
  if (values.hotelRooms) {
    values.hotel_room_ids = values.hotelRooms.map((hotelRoom) =>
      String(hotelRoom.id),
    );
    delete values.hotelRooms;
  }
  return values;
};
